import { getQueryClient } from '@/providers/queryClient';
import { getList } from '@/requests/api/get-list';
import { QUERY_KEYS } from '@/requests/constants';
import type {
  Apartment,
  ApartmentListing,
  Building,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';

export async function handleCustomApartmentListingNestedCase(
  listResponse: TCustomList<ApartmentListing>
): Promise<TCustomList<ApartmentListing & { _apartment?: Apartment }>> {
  if (!listResponse.data.length) return listResponse;

  // Extract unique apartment IDs from marketSpace relations
  // MarketApartment.id === Apartment.id (1:1 mapping)
  const apartmentIdSet = new Set<string>();
  for (const listing of listResponse.data) {
    const id = listing.marketSpace?.id;
    if (id) {
      apartmentIdSet.add(id);
    }
  }

  if (apartmentIdSet.size === 0) return listResponse;

  // Batch-fetch apartments
  const queryClient = getQueryClient();
  const chunkedApartmentIds = chunkIds(apartmentIdSet);

  const apartmentPromises = chunkedApartmentIds.map(async (chunk) =>
    queryClient.ensureQueryData({
      queryKey: [
        'apartment',
        QUERY_KEYS.LIST,
        { ids: chunk, source: 'apartmentlisting-handler' },
      ],
      queryFn: () =>
        getList<Apartment>({
          modelName: 'apartment',
          queryParams: {
            filters: {
              id: { __in: chunk.join(',') },
            },
            pageSize: chunk.length,
          },
        }),
    })
  );

  const apartmentResults = await Promise.allSettled(apartmentPromises);

  const apartmentMap = new Map<string, Apartment>();
  for (const result of apartmentResults) {
    if (result.status === 'fulfilled' && result.value.list) {
      for (const apartment of result.value.list) {
        apartmentMap.set(apartment.id, apartment);
      }
    }
  }

  // Batch-fetch buildings for the apartments
  const buildingIdSet = new Set<string>();
  for (const apartment of apartmentMap.values()) {
    const buildingId = apartment.building?.id;
    if (buildingId) {
      buildingIdSet.add(buildingId);
    }
  }

  if (buildingIdSet.size > 0) {
    const chunkedBuildingIds = chunkIds(buildingIdSet);

    const buildingPromises = chunkedBuildingIds.map(async (chunk) =>
      queryClient.ensureQueryData({
        queryKey: [
          'building',
          QUERY_KEYS.LIST,
          { ids: chunk, source: 'apartmentlisting-handler' },
        ],
        queryFn: () =>
          getList<Building>({
            modelName: 'building',
            queryParams: {
              filters: {
                id: { __in: chunk.join(',') },
              },
              pageSize: chunk.length,
            },
          }),
      })
    );

    const buildingResults = await Promise.allSettled(buildingPromises);

    const buildingMap = new Map<string, Building>();
    for (const result of buildingResults) {
      if (result.status === 'fulfilled' && result.value.list) {
        for (const building of result.value.list) {
          buildingMap.set(building.id, building);
        }
      }
    }

    // Merge buildings onto apartments
    for (const [aptId, apartment] of apartmentMap) {
      const buildingId = apartment.building?.id;
      if (buildingId) {
        const building = buildingMap.get(buildingId);
        if (building) {
          apartmentMap.set(aptId, {
            ...apartment,
            building: building as Apartment['building'],
          });
        }
      }
    }
  }

  // Merge apartment data onto each listing
  return {
    data: listResponse.data.map((listing) => {
      const apartmentId = listing.marketSpace?.id;
      return {
        ...listing,
        _apartment: apartmentId ? apartmentMap.get(apartmentId) : undefined,
      };
    }),
  };
}
